import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import CasinoIcon from "@mui/icons-material/Casino";
import ListIcon from "@mui/icons-material/List";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import { ActivitiesContext } from "../../context/activities/ActivitiesContext";

const ActivitiesAccordion = () => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { activities, openRandomSelector } = useContext(ActivitiesContext);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const handleNavigateToActivities = () => {
    navigate("/activities");
  };

  const handleRandomActivity = (e) => {
    e.stopPropagation();
    openRandomSelector();
  };

  const activityCount = activities?.length || 0;

  return (
    <Accordion
      expanded={expanded}
      onChange={handleToggle}
      sx={{
        background: "rgba(255,255,255,0.95)",
        borderRadius: "20px !important",
        marginBottom: "16px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
        overflow: "hidden",
        "&:before": {
          display: "none",
        },
        "&.Mui-expanded": {
          margin: "0 0 16px 0",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#333" }} />}
        sx={{
          padding: "20px 24px",
          minHeight: "auto",
          "&.Mui-expanded": {
            minHeight: "auto",
          },
          "& .MuiAccordionSummary-content": {
            margin: 0,
            "&.Mui-expanded": {
              margin: 0,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Box
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #d97000 0%, #ff8c1a 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "12px",
              boxShadow: "0 4px 12px rgba(217, 112, 0, 0.3)",
            }}
          >
            <DirectionsRunIcon sx={{ color: "#fff", fontSize: "22px" }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
              Activities
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
              {activityCount === 0
                ? "No activities yet"
                : `${activityCount} ${activityCount === 1 ? "activity" : "activities"}`}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "0 24px 24px 24px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {/* View All Activities Button */}
          <Box
            onClick={handleNavigateToActivities}
            sx={{
              background: "rgba(255,255,255,0.95)",
              borderRadius: "16px",
              padding: "16px 20px",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "12px",
              border: "1px solid rgba(217, 112, 0, 0.2)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                borderColor: "rgba(217, 112, 0, 0.4)",
                "& .activities-accordion__icon-container": {
                  transform: "scale(1.1) rotate(5deg)",
                },
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "3px",
                background: "linear-gradient(135deg, #d97000 0%, #ff8c1a 100%)",
                transform: "scaleX(0)",
                transformOrigin: "left",
                transition: "transform 0.3s ease",
              },
              "&:hover::after": {
                transform: "scaleX(1)",
              },
            }}
          >
            <Box
              className="activities-accordion__icon-container"
              sx={{
                width: "44px",
                height: "44px",
                minWidth: "44px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #d97000 0%, #ff8c1a 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "transform 0.3s ease",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
              }}
            >
              <ListIcon sx={{ color: "#fff", fontSize: "24px" }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "#333",
                  fontSize: "0.9rem",
                  margin: 0,
                  lineHeight: 1.3,
                }}
              >
                View All Activities
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(0,0,0,0.5)",
                  fontSize: "0.75rem",
                }}
              >
                Manage your recurring activities
              </Typography>
            </Box>
          </Box>

          {/* Random Activity Button */}
          <Box
            onClick={handleRandomActivity}
            sx={{
              position: "relative",
              borderRadius: "16px",
              padding: "16px 20px",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "12px",
              border: "1px solid rgba(39, 101, 128, 0.2)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                borderColor: "rgba(39, 101, 128, 0.4)",
                "& .activities-accordion__dice-icon": {
                  transform: "scale(1.1) rotate(360deg)",
                },
              },
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, #197b68 0, #276580 400px, #6a205c 800px, #276580 1200px, #197b68 1600px)",
                backgroundSize: "1600px 100%",
                backgroundRepeat: "repeat-x",
                backgroundPosition: "0 0",
                animation: "gradient-flow 6s linear infinite",
                opacity: 0.35,
                zIndex: 0,
              },
              "@keyframes gradient-flow": {
                from: {
                  backgroundPositionX: "0",
                },
                to: {
                  backgroundPositionX: "-1600px",
                },
              },
            }}
          >
            <Box
              sx={{
                width: "44px",
                height: "44px",
                minWidth: "44px",
                borderRadius: "12px",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "transform 0.3s ease",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(90deg, #197b68 0, #276580 400px, #6a205c 800px, #276580 1200px, #197b68 1600px)",
                  backgroundSize: "1600px 100%",
                  backgroundRepeat: "repeat-x",
                  backgroundPosition: "0 0",
                  animation: "gradient-flow 6s linear infinite",
                },
              }}
            >
              <CasinoIcon
                className="activities-accordion__dice-icon"
                sx={{
                  color: "#fff",
                  fontSize: "24px",
                  position: "relative",
                  zIndex: 1,
                  transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              />
            </Box>
            <Box sx={{ flex: 1, position: "relative", zIndex: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "#333",
                  fontSize: "0.9rem",
                  margin: 0,
                  lineHeight: 1.3,
                }}
              >
                Random Activity
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(0,0,0,0.5)",
                  fontSize: "0.75rem",
                }}
              >
                {activityCount === 0
                  ? "Add activities to get started"
                  : "Pick a random activity to do"}
              </Typography>
            </Box>
          </Box>

          {/* Info note */}
          {activityCount > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(217, 112, 0, 0.06)",
                border: "1px solid rgba(217, 112, 0, 0.15)",
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: "16px",
                  color: "#d97000",
                  flexShrink: 0,
                  mt: 0.5,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(0,0,0,0.55)",
                    fontSize: "0.78rem",
                    lineHeight: 1.4,
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  Activities help you balance work with recreation and self-care
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(0,0,0,0.55)",
                    fontSize: "0.78rem",
                    lineHeight: 1.4,
                    display: "block",
                  }}
                >
                  Unlike tasks, you never really complete them. They're ongoing
                  like hobbies
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ActivitiesAccordion;
